import React from 'react';
import { Box, Typography } from '@mui/material';
import { Treemap, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import ChartCard from './ChartCard';
import { COLORS } from './constants';
import { MostOrderedItem } from './types';

// ---------------------------------------------------------------------------
// Treemap cell renderer
// ---------------------------------------------------------------------------

const ItemsTreemapContent: React.FC<any> = ({ x, y, width, height, name, orders, index }) => {
  if (!name || width < 4 || height < 4) return null;
  const fill = COLORS[(index ?? 0) % COLORS.length];
  const showLabel = width > 50 && height > 32;
  const showValue = width > 50 && height > 48;
  const maxChars = Math.max(3, Math.floor(width / 8));
  const displayName = name.length > maxChars ? name.slice(0, maxChars) + '…' : name;

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" strokeWidth={2} rx={4} ry={4} />
      {showLabel && (
        <text x={x + width / 2} y={y + height / 2 - (showValue ? 8 : 0)} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={Math.min(13, width / 8)} fontWeight={600}>
          {displayName}
        </text>
      )}
      {showValue && (
        <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.85)" fontSize={Math.min(12, width / 10)}>
          ×{orders}
        </text>
      )}
    </g>
  );
};

// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------

const ItemsTreemapTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  const { name, orders, percentage } = data;

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderRadius: '10px',
        p: 1.5,
        minWidth: 180,
        maxWidth: 260,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, mb: 0.5 }}>
        {name}
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, mb: 0.25 }}>
        Orders: <strong>{Number(orders).toLocaleString()}</strong>
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
        Share: <strong>{percentage}%</strong> of total orders
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Chart component
// ---------------------------------------------------------------------------

interface MostOrderedItemsChartProps {
  data: MostOrderedItem[];
  serviceType: string;
}

const MostOrderedItemsChart: React.FC<MostOrderedItemsChartProps> = ({ data, serviceType }) => {
  const title = serviceType === 'room' ? 'Most Booked Room Types' : 'Most Ordered Items';

  return (
    <ChartCard title={title} timeout={1700}>
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={data}
          dataKey="orders"
          nameKey="name"
          aspectRatio={4 / 3}
          content={<ItemsTreemapContent />}
        >
          <RechartsTooltip content={<ItemsTreemapTooltip />} cursor={false} />
        </Treemap>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export default MostOrderedItemsChart;
